import React, { useMemo, useRef, useState } from 'react';
import {
    AscSort,
    DCollapseButton,
    DContextMenu,
    DContextMenuItem,
    DescSort,
    DTable,
    DTableBody,
    DTableBodyCell,
    DTableBodyRow,
    DTableHead,
    DTableHeadCell,
    DTableHeadRow,
    DTableSkeletonRows,
    handleMouseEvent,
    NeutralSort,
    TCustomTableBodyCellTextAlign,
    useDContextMenu,
} from '../../index';
import { Box, Theme, useTheme } from '@mui/material';
import { SxProps } from '@mui/material';

export type SortedOrder = 'asc' | 'desc' | null;

export type CustomTableContextMenuItem<T> = {
    title: string;
    callback: (item: T) => void;
    disable?: (item: T) => boolean;
};

export interface ICustomTableWrapperProps<T> {
    sortedIndex?: number | null;
    setSortedIndex?: (index: number | null) => void;
    sortedOrder?: SortedOrder;
    setSortedOrder?: (sortedOrder: SortedOrder) => void;
    items: {
        // Usually a plain string, but a node is allowed so a column can carry
        // its own header spacing/markup (e.g. a left gutter to separate an
        // adjacent right-aligned column's title from a left-aligned one).
        title: React.ReactNode;
        filter?: React.ReactNode;
        content: (
            dataItem: T,
            index: number,
        ) => React.ReactElement<any, any> | string | number | undefined | null;
        cellGrow?: number;
        textAlign?: TCustomTableBodyCellTextAlign;
        sortable?: boolean;
        cellSx?: (item: T) => SxProps<Theme>;
    }[];
    data: T[];
    // Optional bold summary row pinned under the body (e.g. column totals). One
    // node per `items` column, same order; pass null/undefined for columns with
    // no total (like a label column). When present it renders non-striped with a
    // top divider and keeps the last data row's border.
    totalsRow?: (React.ReactNode | string | number | null | undefined)[];
    // When true and there's no data yet, the body shows skeleton rows instead
    // of the empty state — so the first load reserves the table shape rather
    // than flashing "No hay resultados". Pagination keeps showing prior rows
    // (data stays non-empty), so skeletons only appear on the initial fetch.
    loading?: boolean;
    stickyHeader?: boolean;
    // Bounds the table's own scroll container so the sticky header has something
    // to stick to (see DTable.maxHeight). Use on long, unpaginated tables.
    maxHeight?: number | string;
    // Fill a bounded flex-column parent and be the only vertical scroller
    // (see DTable.fill). Prefer this over maxHeight inside a flex layout.
    fill?: boolean;
    disableSortInteraction?: boolean;
    collapse?: (
        dataItem: T,
    ) => React.ReactElement<any, any> | string | number | undefined | null;
    contextItems?: CustomTableContextMenuItem<T>[];
    defaultOpenCollapse?: boolean;
    padding?: boolean;
    onRowClick?: (item: T) => void;
    onRowDoubleClick?: (item: T) => void;
    rowSx?: (item: T) => SxProps<Theme>;
    stripeColor?: string;
    baseColor?: string;
    // Opt-in: center every column header regardless of the column's content
    // textAlign. Body cells still align by their own textAlign (by type).
    centerHeaders?: boolean;
}

export type ICustomTableWrapperItem = ICustomTableWrapperProps<
    Record<any, any>
>['items'][number];

export function CTableWrapper<T>({
    items,
    data,
    totalsRow,
    loading = false,
    stickyHeader = true,
    maxHeight,
    fill = false,
    disableSortInteraction = false,
    collapse,
    setSortedIndex,
    sortedIndex,
    setSortedOrder,
    sortedOrder,
    contextItems,
    defaultOpenCollapse = false,
    padding = true,
    onRowClick,
    onRowDoubleClick,
    rowSx,
    stripeColor,
    baseColor,
    centerHeaders = false,
}: ICustomTableWrapperProps<T>) {
    const theme = useTheme();
    const rowAlternateColor =
        theme.palette.mode === 'dark' ? '#303030' : '#E0E0E0';
    const cellGrowthTotal = items.reduce(
        (acc, item) => acc + (item?.cellGrow || 1),
        0,
    );
    const [collapseOpen, setCollapseOpen] = useState<null | number>(null);

    const [contextMenu, setContextMenu] = useDContextMenu();
    const clickedItem = useRef<T | null>(null);

    const contextMenuItems: DContextMenuItem[] = useMemo(() => {
        if (!contextItems || !clickedItem) {
            return [];
        }

        return contextItems.map((item) => {
            return {
                ...item,
                callback: () => {
                    if (clickedItem.current) {
                        item.callback(clickedItem.current!);
                    }
                },
                disable: () => {
                    return item.disable && clickedItem.current
                        ? item.disable(clickedItem.current)
                        : (() => false)();
                },
            };
        });
    }, [contextItems]);

    return (
        <>
            <DTable
                padding={padding}
                stickyHeader={stickyHeader}
                maxHeight={maxHeight}
                fill={fill}
            >
                <DTableHead>
                    <DTableHeadRow>
                        {collapse && !defaultOpenCollapse ? (
                            <DTableHeadCell />
                        ) : null}
                        {items.map((item, index) => {
                            const cellGrowth = item.cellGrow || 1;
                            const cellWidth = Math.floor(
                                (cellGrowth / cellGrowthTotal) * 100,
                            );
                            // Header alignment normally matches the body cells so
                            // a left-aligned text column doesn't sit under a
                            // centered title. With centerHeaders the title is
                            // always centered while the body keeps its own
                            // textAlign (aligned by type). Map to flexbox.
                            const cellTextAlign = item.textAlign || 'left';
                            const headerTextAlign = centerHeaders
                                ? 'center'
                                : cellTextAlign;
                            const flexAlign =
                                headerTextAlign === 'right'
                                    ? 'flex-end'
                                    : headerTextAlign === 'center'
                                    ? 'center'
                                    : 'flex-start';
                            return (
                                <DTableHeadCell
                                    key={index}
                                    width={cellWidth}
                                    textAlign={headerTextAlign}
                                    onClick={
                                        item.sortable &&
                                        setSortedOrder &&
                                        setSortedIndex &&
                                        !disableSortInteraction
                                            ? function () {
                                                  if (item.sortable) {
                                                      if (
                                                          sortedIndex ===
                                                              null ||
                                                          sortedIndex !== index
                                                      ) {
                                                          setSortedIndex(index);
                                                          setSortedOrder('asc');
                                                      } else if (
                                                          sortedIndex ===
                                                              index &&
                                                          sortedOrder === 'asc'
                                                      ) {
                                                          setSortedOrder(
                                                              'desc',
                                                          );
                                                      } else if (
                                                          sortedIndex ===
                                                              index &&
                                                          sortedOrder === 'desc'
                                                      ) {
                                                          setSortedOrder(null);
                                                          setSortedIndex(null);
                                                      }
                                                  }
                                              }
                                            : undefined
                                    }
                                >
                                    <Box
                                        component={'span'}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: flexAlign,
                                            gap: 1,
                                            // Fill the cell so alignItems/
                                            // justifyContent actually position the
                                            // title (a content-width box makes the
                                            // centering a no-op).
                                            width: '100%',
                                        }}
                                    >
                                        <Box
                                            component={'span'}
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'nowrap',
                                                justifyContent: flexAlign,
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            {item.title}
                                            {item.sortable ? (
                                                <>
                                                    <Box
                                                        component={'span'}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            ml: 2,
                                                        }}
                                                    >
                                                        {sortedOrder ===
                                                            'asc' &&
                                                        sortedIndex ===
                                                            index ? (
                                                            <AscSort
                                                                fontSize={
                                                                    'small'
                                                                }
                                                            />
                                                        ) : sortedOrder ===
                                                              'desc' &&
                                                          sortedIndex ===
                                                              index ? (
                                                            <DescSort
                                                                fontSize={
                                                                    'small'
                                                                }
                                                            />
                                                        ) : (
                                                            <NeutralSort
                                                                fontSize={
                                                                    'small'
                                                                }
                                                            />
                                                        )}
                                                    </Box>
                                                </>
                                            ) : null}
                                        </Box>
                                        {item.filter ? (
                                            <Box
                                                component={'span'}
                                                sx={{ width: '100%' }}
                                                onClick={(
                                                    e: React.MouseEvent,
                                                ) => e.stopPropagation()}
                                            >
                                                {item.filter}
                                            </Box>
                                        ) : null}
                                    </Box>
                                </DTableHeadCell>
                            );
                        })}
                    </DTableHeadRow>
                </DTableHead>
                <DTableBody>
                    {loading && data.length === 0 ? (
                        <DTableSkeletonRows
                            columns={
                                items.length +
                                (collapse && !defaultOpenCollapse ? 1 : 0)
                            }
                        />
                    ) : data.length > 0 ? (
                        data.map((dataItem, dataIndex) => {
                            return (
                                <React.Fragment key={dataIndex}>
                                    <DTableBodyRow
                                        onClick={
                                            onRowClick
                                                ? () => onRowClick(dataItem)
                                                : undefined
                                        }
                                        onDoubleClick={
                                            onRowDoubleClick
                                                ? () => {
                                                      onRowDoubleClick(
                                                          dataItem,
                                                      );
                                                  }
                                                : undefined
                                        }
                                        alternate={dataIndex % 2 === 1}
                                        alternateColor={stripeColor}
                                        baseColor={baseColor}
                                        onContextMenu={
                                            contextItems
                                                ? (e) => {
                                                      handleMouseEvent(
                                                          e,
                                                          setContextMenu,
                                                          contextMenu,
                                                      );
                                                      clickedItem.current =
                                                          dataItem;
                                                  }
                                                : undefined
                                        }
                                        sx={rowSx ? rowSx(dataItem) : undefined}
                                    >
                                        {collapse && !defaultOpenCollapse ? (
                                            <DTableBodyCell
                                                noBorder={
                                                    dataIndex + 1 ===
                                                    data.length
                                                }
                                            >
                                                <DCollapseButton
                                                    open={
                                                        collapseOpen ===
                                                        dataIndex
                                                    }
                                                    setOpen={() => {
                                                        setCollapseOpen(
                                                            collapseOpen !==
                                                                dataIndex
                                                                ? dataIndex
                                                                : null,
                                                        );
                                                    }}
                                                />
                                            </DTableBodyCell>
                                        ) : null}
                                        {items.map((item, index) => {
                                            return (
                                                <DTableBodyCell
                                                    key={index}
                                                    textAlign={
                                                        item.textAlign || 'left'
                                                    }
                                                    noBorder={
                                                        (dataIndex + 1 ===
                                                            data.length &&
                                                            !totalsRow) ||
                                                        (collapse != null &&
                                                            (defaultOpenCollapse ||
                                                                collapseOpen ===
                                                                    dataIndex))
                                                    }
                                                    sx={
                                                        item.cellSx
                                                            ? item.cellSx(
                                                                  dataItem,
                                                              )
                                                            : undefined
                                                    }
                                                >
                                                    {item.content(
                                                        dataItem,
                                                        dataIndex,
                                                    )}
                                                </DTableBodyCell>
                                            );
                                        })}
                                    </DTableBodyRow>
                                    {collapse &&
                                    (defaultOpenCollapse ||
                                        collapseOpen === dataIndex) ? (
                                        <DTableBodyRow
                                            onContextMenu={
                                                contextItems
                                                    ? (e) => {
                                                          handleMouseEvent(
                                                              e,
                                                              setContextMenu,
                                                              contextMenu,
                                                          );
                                                          clickedItem.current =
                                                              dataItem;
                                                      }
                                                    : undefined
                                            }
                                            sx={
                                                rowSx
                                                    ? rowSx(dataItem)
                                                    : undefined
                                            }
                                        >
                                            <DTableBodyCell
                                                colSpan={
                                                    items.length +
                                                    (!defaultOpenCollapse
                                                        ? 1
                                                        : 0)
                                                }
                                                pt={2}
                                                pb={8}
                                                px={8}
                                                sx={{
                                                    bgcolor:
                                                        dataIndex % 2 === 1
                                                            ? rowAlternateColor
                                                            : 'background.paper',
                                                }}
                                            >
                                                {collapse(dataItem)}
                                            </DTableBodyCell>
                                        </DTableBodyRow>
                                    ) : null}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <DTableBodyRow>
                            <DTableBodyCell
                                colSpan={items.length}
                                textAlign={'center'}
                                noBorder
                            >
                                No hay resultados
                            </DTableBodyCell>
                        </DTableBodyRow>
                    )}
                    {totalsRow && data.length > 0 ? (
                        <DTableBodyRow>
                            {collapse && !defaultOpenCollapse ? (
                                <DTableBodyCell noBorder fontWeight={'bold'} />
                            ) : null}
                            {items.map((item, index) => (
                                <DTableBodyCell
                                    key={index}
                                    noBorder
                                    fontWeight={'bold'}
                                    textAlign={item.textAlign || 'left'}
                                    sx={{
                                        borderTop: '2px solid',
                                        borderTopColor: 'divider',
                                    }}
                                >
                                    {totalsRow[index]}
                                </DTableBodyCell>
                            ))}
                        </DTableBodyRow>
                    ) : null}
                </DTableBody>
            </DTable>
            {contextItems && contextItems.length > 0 ? (
                <DContextMenu
                    contextMenu={contextMenu}
                    setContextMenu={setContextMenu}
                    contextMenuItems={contextMenuItems}
                    onClose={() => {
                        // clickedItem.current = null;
                    }}
                />
            ) : null}
        </>
    );
}
